/**
 * Admin - Vibe Margin Reconciliation API
 *
 * Read-only operator window onto the L5 reconciliation: given an org and a
 * period, run the L2 rating engine for the billed revenue and sum the L1 AWS
 * cost ledger for the measured cost, then report the margin. Answers "this org
 * would be billed $Y, it cost us $X, margin Z%".
 *
 * Vibe convention: no admin-panel UI for v1, route through the CLI / curl. No
 * write surface. The period defaults to the current UTC billing month when the
 * query omits `windowStart` / `windowEnd`.
 *
 * Margin is reported in USD ten-thousandths (the cost side is natively USD);
 * `revenueCurrency` + `revenueTenThousandths` carry the billed total in the
 * policy currency, and `fxRate` is the snapshot used to convert it to USD.
 */

import { z } from "zod";

import { VibeCurrencyReadSchema } from "../../../shared/domain/vibe/rating/money";
import { ApiSuccessSchema } from "../../common/response.schema";

export const VibeMarginReconResponseSchema = z.object({
  organizationId: z.string(),
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  /// Instant the pricing policy + FX were resolved at (defaults to windowStart).
  at: z.string().datetime(),
  /// FX snapshot used to re-express the billed total in USD for the margin math.
  fxRate: z.object({
    targetCurrency: VibeCurrencyReadSchema,
    /// Target-currency units per 1 USD (1 for a USD policy).
    usdToTargetRate: z.number().positive(),
    asOf: z.string().datetime(),
    source: z.string()
  }),
  /// Billed revenue currency (the policy currency).
  revenueCurrency: VibeCurrencyReadSchema,
  /// Billed revenue in ten-thousandths of `revenueCurrency` (markup + fee + FX baked in).
  revenueTenThousandths: z.number().int(),
  /// Billed revenue re-expressed in USD ten-thousandths (apples-to-apples with cost).
  revenueUsdTenThousandths: z.number().int(),
  /// Measured AWS cost over the window in USD ten-thousandths (latest snapshot per fact).
  costUsdTenThousandths: z.number().int(),
  /// Gross margin in USD ten-thousandths (`revenueUsd - costUsd`); negative when cost exceeds revenue.
  marginUsdTenThousandths: z.number().int(),
  /// Gross margin as % of revenue, two decimals; null when revenue is zero.
  marginPct: z.number().nullable()
});

export const VibeMarginReconQuerySchema = z
  .object({
    /// ISO-8601; defaults to the start of the current UTC billing month.
    windowStart: z.string().datetime().optional(),
    /// ISO-8601; defaults to the start of the next UTC billing month (half-open).
    windowEnd: z.string().datetime().optional()
  })
  // Both-or-neither: a single bound would mix a caller instant with the
  // server's current-month default, yielding an inverted or far-too-wide window.
  .refine((q) => (q.windowStart === undefined) === (q.windowEnd === undefined), {
    message: "windowStart and windowEnd must be supplied together"
  })
  .refine(
    (q) =>
      q.windowStart === undefined ||
      q.windowEnd === undefined ||
      // Compare as instants, not raw strings — a lexical compare mishandles
      // equal instants at different fractional precision and any offset form.
      Date.parse(q.windowStart) < Date.parse(q.windowEnd),
    {
      message: "windowStart must be strictly before windowEnd"
    }
  );

export const VibeMarginReconPathVarsSchema = z.object({
  organizationId: z.string().min(1)
});

/// One org's reconciled margin line in the fleet view (USD-blended).
const VibeFleetMarginOrgLineSchema = z.object({
  organizationId: z.string(),
  /// Display name for the expense table. Null when the org row is gone (cost
  /// lines outlive a deleted org) — the UI falls back to the id.
  /// `.catch(null)` so a newer SPA against an older backend degrades.
  organizationName: z.string().nullable().catch(null),
  revenueCurrency: VibeCurrencyReadSchema,
  revenueTenThousandths: z.number().int(),
  revenueUsdTenThousandths: z.number().int(),
  costUsdTenThousandths: z.number().int(),
  marginUsdTenThousandths: z.number().int(),
  marginPct: z.number().nullable()
});

export const VibeFleetMarginReconResponseSchema = z.object({
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  /// Distinct orgs with usage in the window — the reconciliation candidates.
  orgsConsidered: z.number().int(),
  /// Orgs whose per-org reconciliation failed — counted, never abort the batch.
  failed: z.number().int(),
  orgs: z.array(VibeFleetMarginOrgLineSchema),
  /// Fleet totals in USD ten-thousandths (every org's revenue converted via its own FX snapshot).
  totals: z.object({
    totalRevenueUsdTenThousandths: z.number().int(),
    totalCostUsdTenThousandths: z.number().int(),
    totalMarginUsdTenThousandths: z.number().int(),
    /// Blended margin as % of total revenue; null when fleet revenue is zero.
    blendedMarginPct: z.number().nullable()
  })
});

export const ZAdminVibeMarginRecon = {
  ReconcileVibeMargin: {
    method: "GET" as const,
    path: "/api/admin/vibe/margin-recon/:organizationId",
    PathVars: VibeMarginReconPathVarsSchema,
    Query: VibeMarginReconQuerySchema,
    Response: ApiSuccessSchema(VibeMarginReconResponseSchema)
  },
  ReconcileVibeFleetMargin: {
    method: "GET" as const,
    path: "/api/admin/vibe/margin-recon",
    Query: VibeMarginReconQuerySchema,
    Response: ApiSuccessSchema(VibeFleetMarginReconResponseSchema)
  }
} as const;

export type VibeMarginReconResponse = z.infer<typeof VibeMarginReconResponseSchema>;
export type VibeMarginReconQuery = z.infer<typeof VibeMarginReconQuerySchema>;
export type VibeFleetMarginReconResponse = z.infer<typeof VibeFleetMarginReconResponseSchema>;
